/**
 * DetailsTab - Configuration tab for challenge_sequence follow-ups.
 * Redesigned with collapsible sections, green accent, and BR phone formatting.
 */
import React, { useState } from 'react';
import {
  Send, Calendar, Users, Play, Check, Loader2,
  RotateCcw, Settings, Plus, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, MessageSquare, AlertCircle, UserPlus, Trash2,
} from 'lucide-react';
import InlineEditField from './InlineEditField';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import TemplatePicker from '../messages/TemplatePicker';
import { templateCapabilities } from '../../utils/templateParams';
import { followupPhoneToRaw, isBrPhoneInput, maskFollowupPhoneInput } from './followupPhone';

const StatusBadge = ({ status, colors }) => {
  const config = {
    draft: { label: 'Rascunho', bg: `${colors.textSecondary}20`, color: colors.textSecondary },
    active: { label: 'Ativo', bg: '#22c55e20', color: '#22c55e' },
    paused: { label: 'Pausado', bg: '#f59e0b20', color: '#f59e0b' },
    completed: { label: 'Concluido', bg: '#6366f120', color: '#6366f1' },
  };
  const c = config[status] || config.draft;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
      fontWeight: 600, backgroundColor: c.bg, color: c.color,
    }}>
      {c.label}
    </span>
  );
};

/** Reusable collapsible section */
const CollapsibleSection = ({ title, icon: Icon, defaultOpen = true, children, colors, accentColor }) => {
  const [open, setOpen] = useState(defaultOpen);
  const accent = accentColor || colors.accentPrimary;
  return (
    <div style={{
      backgroundColor: colors.bgPrimary, borderRadius: '12px',
      border: `1px solid ${colors.borderColor}`,
      borderLeft: `3px solid ${accent}`,
      marginBottom: '16px', overflow: 'hidden',
    }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '14px 16px',
        }}
      >
        <span style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          fontSize: '14px', fontWeight: 600, color: colors.textPrimary,
        }}>
          {Icon && <Icon size={16} color={accent} />}
          {title}
        </span>
        {open ? (
          <ChevronUp size={16} color={colors.textSecondary} />
        ) : (
          <ChevronDown size={16} color={colors.textSecondary} />
        )}
      </button>
      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

const DAYS_PER_PAGE = 30;
const MAX_DAYS = 90;

// Green accent for challenge_sequence
const ACCENT = '#22c55e';

const DetailsTab = ({
  followup,
  history,
  colors,
  styles,
  testNumber,
  setTestNumber,
  sendingDay,
  onSendDay,
  onResetDay,
  onActivate,
  onDeactivate,
  onUpdateFollowup,
  onOpenDayEditor,
  onAddDay,
  onRemoveDay,
  isBusinessChannel,
  approvedTemplates = [],
  onOpenContactPicker,
}) => {
  const [currentMonth, setCurrentMonth] = useState(1);
  // Seletor único de template (2026-09-14). Sem a flag, como antes.
  const { flags } = useFeatureFlags();
  const templateUi = Boolean(flags.template_params_ui);
  const f = followup;
  const days = f.days || {};

  const allDays = Array.from({ length: f.total_days }, (_, i) => String(i + 1));

  const totalMonths = Math.max(1, Math.ceil(f.total_days / DAYS_PER_PAGE));
  const showMonthPagination = f.total_days > DAYS_PER_PAGE;

  const monthStart = (currentMonth - 1) * DAYS_PER_PAGE + 1;
  const monthEnd = Math.min(currentMonth * DAYS_PER_PAGE, f.total_days);

  const visibleDays = showMonthPagination
    ? allDays.filter(k => {
        const n = Number(k);
        return n >= monthStart && n <= monthEnd;
      })
    : allDays;

  // Máscara BR para DDD + número; com "+" ou com DDI (12 a 15 dígitos) vale
  // como veio (followupPhone.js).
  const handleTestNumberChange = (e) => {
    const masked = maskFollowupPhoneInput(e.target.value);
    if (masked !== null) setTestNumber(masked);
  };

  // Número para a API, com DDI ('' enquanto não for um número válido).
  const getTestNumberRaw = () => followupPhoneToRaw(testNumber);
  const brInput = isBrPhoneInput(testNumber);

  const testRaw = getTestNumberRaw();
  const sentDays = new Set();
  history.forEach(h => {
    if (h.status === 'sent' && h.recipient === testRaw) sentDays.add(h.day);
  });

  const handleAddDay = () => {
    if (f.total_days >= MAX_DAYS) return;
    const newDay = f.total_days + 1;
    const newMonth = Math.ceil(newDay / DAYS_PER_PAGE);
    setCurrentMonth(newMonth);
    onAddDay(newDay);
  };

  const canAddDay = f.total_days < MAX_DAYS;
  const addDayInCurrentPage = !showMonthPagination || currentMonth === totalMonths;

  // Configured days count
  const configuredDays = Object.values(days).filter(d => d.messages && d.messages.length > 0).length;

  return (
    <>
      {/* Summary Card */}
      <div style={{
        ...styles.card, marginBottom: '20px', cursor: 'default',
        borderLeft: `4px solid ${ACCENT}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ flex: 1, marginRight: '12px' }}>
            <InlineEditField
              value={f.name}
              onSave={(val) => onUpdateFollowup({ name: val })}
              placeholder="Nome do follow-up"
              colors={colors}
              textStyle={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}
              inputStyle={{ fontSize: '18px', fontWeight: 600 }}
            />
            <div style={{ marginTop: '4px' }}>
              <InlineEditField
                value={f.description}
                onSave={(val) => onUpdateFollowup({ description: val })}
                placeholder="Adicionar descrição..."
                multiline
                colors={colors}
                textStyle={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}
                inputStyle={{ fontSize: '13px' }}
              />
            </div>
          </div>
          <StatusBadge status={f.status} colors={colors} />
        </div>

        {/* Summary info */}
        <div style={{
          padding: '8px 12px', borderRadius: '8px',
          backgroundColor: `${ACCENT}08`, border: `1px solid ${ACCENT}15`,
          fontSize: '13px', color: ACCENT, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: '6px',
          marginTop: '8px',
        }}>
          <Calendar size={14} />
          Sequência de {f.total_days} dias ({configuredDays} configurados)
        </div>

        <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: colors.textSecondary, marginTop: '10px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Users size={12} /> {(f.recipients || []).length} destinatário{(f.recipients || []).length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* 1. Days Grid */}
      <CollapsibleSection title="Dias e Mensagens" icon={MessageSquare} defaultOpen={true} colors={colors} accentColor={ACCENT}>
        {/* Month Pagination */}
        {showMonthPagination && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '12px',
          }}>
            <button
              onClick={() => setCurrentMonth(prev => Math.max(1, prev - 1))}
              disabled={currentMonth === 1}
              style={{
                ...styles.btn, ...styles.btnOutline,
                opacity: currentMonth === 1 ? 0.4 : 1,
                cursor: currentMonth === 1 ? 'default' : 'pointer',
                padding: '6px 12px', fontSize: '12px',
              }}
            >
              <ChevronLeft size={14} /> Anterior
            </button>
            <span style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
              Mes {currentMonth} de {totalMonths}
              <span style={{ fontWeight: 400, color: colors.textSecondary, marginLeft: '8px', fontSize: '12px' }}>
                (Dias {monthStart}-{monthEnd})
              </span>
            </span>
            <button
              onClick={() => setCurrentMonth(prev => Math.min(totalMonths, prev + 1))}
              disabled={currentMonth === totalMonths}
              style={{
                ...styles.btn, ...styles.btnOutline,
                opacity: currentMonth === totalMonths ? 0.4 : 1,
                cursor: currentMonth === totalMonths ? 'default' : 'pointer',
                padding: '6px 12px', fontSize: '12px',
              }}
            >
              Próximo <ChevronRight size={14} />
            </button>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
          {visibleDays.map(dayKey => {
            const dayNum = Number(dayKey);
            const rawTest = getTestNumberRaw();
            const isSent = sentDays.has(dayNum);
            const isSending = sendingDay === dayNum;
            const msgCount = (days[dayKey]?.messages || []).length;
            return (
              <div key={dayKey} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                padding: '10px', borderRadius: '10px',
                backgroundColor: isSent ? `${ACCENT}10` : msgCount > 0 ? colors.bgSecondary : `${colors.bgSecondary}80`,
                border: `1px solid ${isSent ? `${ACCENT}40` : colors.borderColor}`,
                position: 'relative',
              }}>
                <div style={{
                  position: 'absolute', top: '6px', right: '6px',
                  display: 'flex', gap: '2px',
                }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); onRemoveDay(dayNum); }}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: colors.textTertiary, padding: '2px',
                      borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'color 0.15s',
                    }}
                    title="Remover dia"
                    onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                    onMouseLeave={e => e.currentTarget.style.color = colors.textTertiary}
                  >
                    <Trash2 size={13} />
                  </button>
                  <button
                    onClick={() => onOpenDayEditor(dayNum)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: colors.textTertiary, padding: '2px',
                      borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'color 0.15s',
                    }}
                    title="Configurar mensagens"
                    onMouseEnter={e => e.currentTarget.style.color = ACCENT}
                    onMouseLeave={e => e.currentTarget.style.color = colors.textTertiary}
                  >
                    <Settings size={14} />
                  </button>
                </div>

                <span style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                  Dia {dayNum}
                </span>
                <span style={{ fontSize: '11px', color: msgCount > 0 ? colors.textSecondary : colors.textTertiary, display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <MessageSquare size={10} /> {msgCount} msg{msgCount !== 1 ? 's' : ''}
                </span>
                {msgCount > 0 && (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => onSendDay(dayNum)}
                      disabled={isSending || !rawTest}
                      style={{
                        ...styles.btn, padding: '4px 10px', fontSize: '11px',
                        backgroundColor: isSent ? ACCENT : ACCENT,
                        color: 'white', opacity: isSending || !rawTest ? 0.5 : 1,
                      }}
                    >
                      {isSending ? <Loader2 size={12} /> : isSent ? <Check size={12} /> : <Send size={12} />}
                      {isSending ? '...' : isSent ? 'Enviado' : 'Enviar'}
                    </button>
                    {isSent && (
                      <button
                        onClick={() => onResetDay(dayNum)}
                        style={{ ...styles.btn, ...styles.btnOutline, padding: '4px 8px', fontSize: '11px' }}
                        title="Resetar para reenviar"
                      >
                        <RotateCcw size={12} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {canAddDay && addDayInCurrentPage && (
            <div
              onClick={handleAddDay}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '8px', padding: '10px', borderRadius: '10px',
                border: `2px dashed ${ACCENT}40`,
                backgroundColor: 'transparent',
                cursor: 'pointer', minHeight: '90px',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = ACCENT;
                e.currentTarget.style.backgroundColor = `${ACCENT}08`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = `${ACCENT}40`;
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Plus size={20} color={ACCENT} />
              <span style={{ fontSize: '12px', fontWeight: 500, color: ACCENT }}>
                Dia {f.total_days + 1}
              </span>
              <span style={{ fontSize: '10px', color: colors.textTertiary }}>
                {f.total_days}/{MAX_DAYS}
              </span>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* 2. Test Manual */}
      <CollapsibleSection title="Teste manual" icon={Send} defaultOpen={false} colors={colors} accentColor={colors.textSecondary}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            {brInput && (
              <span style={{
                position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                fontSize: '14px', color: colors.textTertiary, fontWeight: 500, pointerEvents: 'none',
              }}>
                +55
              </span>
            )}
            <input
              value={testNumber}
              onChange={handleTestNumberChange}
              placeholder="(11) 99999-9999"
              title="Número do Brasil com DDD, ou de outro país começando com +"
              style={{ ...styles.input, flex: 1, paddingLeft: brInput ? '46px' : '12px' }}
              maxLength={20}
            />
          </div>
          {onOpenContactPicker && (
            <button
              onClick={onOpenContactPicker}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                ...styles.btn, ...styles.btnOutline, padding: '8px 12px', fontSize: '12px',
              }}
              title="Buscar contato"
            >
              <UserPlus size={14} />
            </button>
          )}
        </div>
        <p style={{ fontSize: '11px', color: colors.textTertiary, margin: '6px 0 0' }}>
          Selecione um número e use os botões "Enviar" nos cards de dia acima para testar.
        </p>
      </CollapsibleSection>

      {/* 3. Activation / Status */}
      <CollapsibleSection
        title={f.status === 'active' ? 'Status' : (f.status === 'paused' ? 'Retomar' : 'Ativar')}
        icon={f.status === 'active' ? Check : Play}
        defaultOpen={f.status !== 'active'}
        colors={colors}
        accentColor={f.status === 'active' ? ACCENT : ACCENT}
      >
        {f.status === 'active' ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 500, color: ACCENT, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Check size={14} /> Follow-up ativo
              </p>
              <p style={{ fontSize: '12px', color: colors.textSecondary, margin: 0 }}>
                Cada destinatário recebe as mensagens do dia 1 ao dia {f.total_days}, a partir da data em que foi adicionado.
              </p>
            </div>
            <button onClick={onDeactivate} style={{
              ...styles.btn, padding: '8px 16px', fontSize: '13px',
              backgroundColor: 'transparent', color: '#f59e0b',
              border: '1px solid #f59e0b40', display: 'flex', alignItems: 'center', gap: '4px',
            }}>
              <AlertCircle size={14} /> Pausar
            </button>
          </div>
        ) : (
          <>
            <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '0 0 12px' }}>
              Ao ativar, cada destinatário começa a receber as mensagens do dia 1 ao dia {f.total_days}, de forma independente.
            </p>
            <button onClick={onActivate} style={{
              ...styles.btn, ...styles.btnPrimary, padding: '10px 20px', fontSize: '14px',
              backgroundColor: ACCENT, display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              <Play size={16} /> {f.status === 'paused' ? 'Retomar' : 'Ativar'}
            </button>
          </>
        )}
      </CollapsibleSection>

      {/* 4. Advanced Config (WhatsApp Business only) */}
      {isBusinessChannel && (
        <CollapsibleSection title="Configurações avançadas" icon={Settings} defaultOpen={false} colors={colors} accentColor={colors.textSecondary}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                Template de Fallback (janela 24h fechada)
              </label>
              {templateUi ? (
                <TemplatePicker
                  templates={approvedTemplates}
                  value={f.business_config?.fallback_template_name || ''}
                  language={f.business_config?.fallback_template_language || ''}
                  onChange={(tpl) => onUpdateFollowup({
                    business_config: {
                      ...f.business_config,
                      fallback_template_name: tpl?.name || '',
                      fallback_template_language: tpl?.language || 'pt_BR',
                    },
                  })}
                  capabilities={templateCapabilities('fallback', flags)}
                  colors={colors}
                  noneLabel="Nenhum (não enviar fallback)"
                />
              ) : (
              <select
                value={f.business_config?.fallback_template_name || ''}
                onChange={e => onUpdateFollowup({
                  business_config: { ...f.business_config, fallback_template_name: e.target.value }
                })}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                  color: colors.textPrimary, fontSize: '14px', outline: 'none',
                }}
              >
                <option value="">Nenhum (não enviar fallback)</option>
                {approvedTemplates.map(t => (
                  <option key={t.name} value={t.name}>{t.name} ({t.language})</option>
                ))}
              </select>
              )}
              <p style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '4px' }}>
                Quando o conteúdo custom falhar por janela de 24h, este template será enviado como alternativa.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                  Intervalo de retentativa (horas)
                </label>
                <input
                  type="number"
                  min={1}
                  max={168}
                  value={f.business_config?.retry_interval_hours || 6}
                  onChange={e => onUpdateFollowup({
                    business_config: { ...f.business_config, retry_interval_hours: parseInt(e.target.value) || 6 }
                  })}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px',
                    border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                    color: colors.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                  Maximo de tentativas
                </label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={f.business_config?.max_retries || 3}
                  onChange={e => onUpdateFollowup({
                    business_config: { ...f.business_config, max_retries: parseInt(e.target.value) || 3 }
                  })}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px',
                    border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                    color: colors.textPrimary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <p style={{ fontSize: '12px', color: colors.textSecondary, lineHeight: '1.5', margin: 0 }}>
              Se o envio do conteúdo falhar por janela de 24h, o sistema tentará novamente a cada{' '}
              <strong>{f.business_config?.retry_interval_hours || 6}h</strong>, ate{' '}
              <strong>{f.business_config?.max_retries || 3}x</strong>. Após esgotar, o lead será marcado como "não respondeu".
            </p>
          </div>
        </CollapsibleSection>
      )}
    </>
  );
};

export default DetailsTab;
